/**
 *
 * Register the boolean flags that clients can request.
 * useFeatureFlagBool.ts reads this registry and defaults missing flags to false.
 *
 * Insert new flags alphabetically inside the keep-sorted block they belong to, with a date and a
 * description. The dates help us audit the feature flags for clean-up. `make check-keep-sorted`
 * (also run by pre-commit and CI) enforces the order; `make check-keep-sorted-fix` reorders.
 *
 * https://docs.launchdarkly.com/home/getting-started/feature-flags
 */

export const BOOL_FEATURE_FLAGS = [
  // PERMENANT FLAGS - Internal only flag, these are for feature you only want to ship to
  // ModernLoop orgs for testing purposes. Ex. 1-min interview duration.
  // keep-sorted:begin
  // Whether or not to show our Health check banner
  'health_check_banner',

  'MLOnly',

  // 2024-11-26 (Cam) - Flag to show candidate pulse survey metrics
  'prd-205-candidate-pulse-survey-DUMMY-DATA',

  // 2023-10-05 (Cam) - Flag that shows the old debrief section and allows create without tasks
  'user_allow_non_task_debriefs',

  // Lets user connect user from ats to a user in ModernLoop.
  // Primarily for Lyra.
  'user_ats_user_select',

  // Whether or not to hide cancelled tasks. This is used for AE demos.
  'user_hide_cancelled_tasks',

  // 2023-05-22 (Cam) - Gate whether or not to skip adding hiring managers to the slack channel by default
  // This is a special flag for Brex and should move to org pref long-term.
  'user_skip_adding_slack_hiring_managers',
  // keep-sorted:end

  // END PERMENANT FLAGS

  // keep-sorted:begin
  // 2026-08-17 (ctriolo) - Gate the agent account access card (Google/Microsoft OAuth for the
  // AI agent) in AI org settings
  'AI_AGENT_ACCOUNT_ACCESS',

  // AI kill switch — prevents all LLM calls
  'AI_ENABLED',

  // Recruiter-facing global agent entry point and streaming side panel
  'AI_GLOBAL_AGENT',

  // 2026-06-23 (ctriolo) - Gate AI model family and BYO LLM org settings
  'AI_MODEL_SELECTION',

  // AI agent task delegation UI (delegate chip, delegate filter, sidepanel)
  'AI_TASK_AGENTS',

  // 2026-07-23 (ctriolo) - Gate hiring dashboard dark mode UI and persisted theme preference
  'DARK_MODE',

  // 2023-12-12 (hemant) - Flag to show a blocking screen to user if we do not have gmail.send permission
  // as part of delegated auth or Workspace app and user has not gone through OAuth flow
  // Not released at all, what's the plan?
  'google-workspace-app-user-auth-block',

  // 2026-07-31 (ctriolo) - Gate interview module training notifications and the module
  // subscribers assignment UI (FEAT-1720)
  'interview_module_notifications',

  // 2026-08-20 (namir) - Gate the estimated-availability tab on the interviewer profile
  // (ENG-27041). Create this flag only after the backend query is deployed everywhere.
  'INTERVIEWER_AVAILABILITY_TAB',

  // 2026-07-15 (namir) - Gate attachments in the calendar-invite template editors (ENG-25858)
  'invite-template-attachments',

  // 2026-07-09 (team) - Gate bulk invite by email / CSV entry point on Settings > Members
  'invite_by_email',

  // Mentions — @mention UI in activity feed and mention notification delivery
  'MENTIONS',

  // 2025-04-15 (ctriolo) - Microsoft Service User
  'microsoft_service_user',

  // 2026-10-01 (namir) - Select the Microsoft directory access mode in multi-domain settings
  'multi_domain_app_only_directories',

  'NEW_ACTIVITY_FEEDS',

  // In-app notification bell in the nav sidebar
  'NOTIFICATIONS_IN_APP_BELL',

  // 2025-12-11 (namir) - Add additional attendees to event invites
  'org_add_additional_event_attendees',

  // 2024-11-14 (cam) - Add transcript interview bot
  'org_add_interview_bot',

  // 2026-09-10 (yaron) - Ashby reset keeps ModernLoop interviewers and settings by remote ATS ID
  // (FEAT-1661). Gates the reset dialog copy; the backend flag has the same key.
  'org_ashby_reset_retains_interview_plan',

  // 2026-10-14 (yaron) - Gate async ATS schedule operations and sync retry actions
  'org_async_ats_schedule_ops',

  // 2026-09-16 (yaron) - Keep interview plan scorecards and durations in sync from the ATS (Ashby first). Gates the org card, the job stage row, and the backend refresh; the backend flag has the same key.
  'org_ats_scorecard_sync',

  // 2024-07-10 (hemant) - Interview plan break improvements
  'org_break_improvements',

  // 2025-10-23 (namir) - Bulk anonymize candidates
  'org_bulk_anonymize_candidates',

  // 2026-03-25 (namir) - Portal Config settings (PRD-271)
  'org_candidate_portal_configs',

  // 2026-07-10 (namir) - Gate the Collaboration Links org settings page
  'org_collaboration_link_settings',

  // 2026-07-28 (namir) - Gate the company days off CSV bulk import card (FEAT-1232)
  'org_company_days_off_bulk_upload',

  // 2026-06-25 (namir) - Gate the Translations settings page (Content Translations feature)
  'org_content_translations',

  // 2026-07-28 - Default the Zoom host when scheduling a debrief.
  'org_debrief_default_zoom_host',

  // 2026-04-02 (namir) - Job stage Communication tab: debrief additional recipients + org sync
  'org_debrief_event_additional_attendees_settings',

  // 2026-08-07 (namir) - Write debriefs to the ATS as scheduled interviews (FEAT-1647)
  'org_debrief_write_to_ats',

  // 2024-10-28 (hemant) - Settings for saving who to include in Slack channel while creating schedule
  'org_default_chat_attendees',

  // 2024-12-06 (hemant) - #prd-222-enforce-an-org-default-per-template-type
  'org_default_comms_template',

  // 2024-10-23 (kaushal) - Feedback writing time
  'org_feedback_writing_time_event',

  // 2026-09-23 - Gate the Greenhouse job sync scope dialog and its entry points (ENG-27746)
  'org_greenhouse_job_sync_scope',

  // 2026-09-30 - Show the "Jobs in selected departments" option in the Greenhouse jobs-to-sync dialog (ENG-27746)
  'org_greenhouse_job_sync_scope_departments',

  // 2024-01-16 (gaurav/kunal) -Orphaned  tasks
  'org_include_orphaned_tasks_for_application',

  // 2026-08-03 - Gate dedicated settings pages for connected non-coding integrations
  'org_integration_settings_pages',

  // 2026-05-21 (cam) - Show Knowledge bases in Organization Settings
  'org_knowledge_bases',

  // 2025-03-12 (Cam) - Extension parsing logic
  'org_log_extension_parsing',

  // 2025-11-04 (namir) - Add "Log a reschedule" button to schedule success page
  'org_log_reschedule_on_schedule_success_page',

  // 2026-08-01 (chris) - Gate manual re-sync actions for ATS-backed entities (FEAT-1721)
  'org_manual_entity_sync',

  // 2026-07-20 (ctriolo) - Gradually roll out member-preferred employee filters (FEAT-1418)
  'org_member_preferred_employee_filters',

  // 2024-05-22 (hemant) - Adding support for Microsoft GCC High
  'org_microsoft_gcc_high',

  // 2024-11-12 (hemant) - Change the manual schedule flow slack card to the single attendees list design
  'org_new_slack_card',

  // 2026-08-11 (namir) - Send one additional-attendee list per interview, with a role for each
  // recipient. Off keeps the merged schedule-level list the request fields carried before. Create
  // this flag only after the backend is deployed everywhere.
  'org_per_interview_additional_attendees',

  // 2026-07-21 (namir) - Gate the admin Privacy Audit Log settings page, its GraphQL query, and CSV
  // export (ENG-25870)
  'org_privacy_audit_log_ui',

  // 2026-07-16 (ctriolo) - Gate RSVP lifecycle task flags and related notification-preference UI
  'org_rsvp_lifecycle_task_flags',

  // 2024-03-08 (Yaron) - SAML and SCIM Self Serve
  'org_saml_scim_self_serve',

  // 2026-09-03 (namir) - Replace the self-schedule timeframe weeks dropdown with an integer days
  // input, range 1 to 60 (FEAT-576)
  'org_self_schedule_timeframe_in_days',

  // 2026-07-09 (chris) - Show Debriefs tab on the Metrics page
  'org_show_debrief_metrics',

  // 2024-12-16 (cam) - Metrics maintenance
  'org_show_metrics_maintenance',

  // 2025-12-03 (namir) - Show last sent at column in communication templates table
  'org_show_template_last_sent_at_column',

  // 2025-03-22 (cam) - Show time to schedule metrics
  'org_show_time_to_schedule_metrics',

  // 2026-08-10 (chris) - Skip the calendar write-permission gate so schedulers can enter
  // update/reschedule flows for events on calendars they can no longer access
  // (e.g. after a Microsoft-to-Google migration)
  'org_skip_calendar_permission_check',

  // 2026-07-28 (namir) - Gate rendering debriefs on the Upcoming Events page
  'org_upcoming_events_debriefs',

  // 2025-12-08 (ctriolo) - Show conflict highlighting in update schedule flow
  'org_update_schedule_conflict_highlighting',

  // 2025-10-10 (namir) - Modern Loop AI Chatbot
  'org_use_modern_loop_ai_chatbot',

  // 2026-07-16 (namir) - Gate the video meeting link scope (one link vs. unique per interview) UI
  'org_video_meeting_link_scope',

  //  2024-11-25 (Ashutosh ) - Enabling Workdays worker data
  'org_workday_use_workers_data_report',

  // 2026-09-15 - Gate the organization Zoom meeting topic settings page
  'org_zoom_meeting_topic_settings',

  // 2024-12-04 (Naveen) - Flag for PRD 155 - Codesignal template select
  'prd-155-codesignal-template-select',

  // 2024-10-21 (cam) - Candidate pulse survey
  'prd-205-candidate-pulse-survey',

  // 2024-12-11 (cam) - Brighthire integration
  'prd-249-brighthire-integration',

  // 2024-11-18 (Ashutosh) - Sourcing link v2
  'prd_228_sourcing_link_status_chip',

  // 2024-10-03 (hemant) - Flag for PRD 81 - multi interview self scheduling
  'prd_81_multi_interview_self_scheduling',

  // 2026-06-22 (ctriolo) - Gate the Session timeout org settings page
  'SESSION_INACTIVITY_TIMEOUT',

  // 2025-10-07 (chris) - Downloads
  'settings_downloads',

  // 2026-04-30 (yaron) - Show Greenhouse Harvest V3 banner and dialog prompts
  'show_greenhouse_v3_ui_prompts',

  // 2026-05-20 (yaron) - Show Kula ATS integration in Settings > Integrations
  'show_kula_ats_integration',

  // 2026-07-15 (namir) - Gate attachments in the Slack template editors (ENG-25858)
  'slack-template-attachments',

  // 2025-02-17 (gaurav) - Smart recruiters new sourcing link permissions
  'smart_recruiters_new_sourcing_link_permissions',

  // 2026-07-06 (ctriolo) - Gate the Statuspage API-backed in-app status banner
  'status_page_banner',

  // 2026-09-09 (yaron) - Gate the "book if a perfect schedule is found" setting UI surfaces
  // (org toggle, job-stage row, request-flow toggle) (ENG-26870)
  'TASK_AUTO_BOOK_PERFECT_SCHEDULE',

  // Two-column schedule task timeline layout
  'TASK_TWO_COLUMN_TIMELINE_VIEW',

  // Open task details inside the Chrome extension sidebar
  'TASK_VIEW_IN_EXTENSION',

  // 2026-06-04 - Apply attribute filters to trainee pool on module seats (FEAT-488)
  'trainee_attribute_filtering',

  // 2024-12-15 - Use only ModernLoop metrics data (exclude ATS data from tooltips)
  'use-only-modernloop-metrics-data',

  // 2025-06-24 (cam) - Async live portals
  'user_async_live_portals',

  // 2026-02-05 (ctriolo) - Use table-based view for All Candidates tab
  'user_candidates_table_view',

  // 2023-03-29 - Flag to gate the Custom {{INTERVIEW_SCHEDULE}} template in Org settings Templates page.
  // This is in cold-storage essentially.
  'user_custom_interview_schedule_template',

  // 2025-02-13 (Ashutosh) - Disable batch for quick task validate
  'user_disable_batching_for_quick_task_validate',

  // 2024-09-18 (kalpak) - Sync Interviewer Properties (Google + Microsoft + SCIM)
  'user_display_candidate_first_availability_on_mytask',

  // 2026-02-06 (ctriolo) - Use table-based view for Employees/Interviewers page
  'user_employees_table_view',

  // 2024-04-08 (Ashutosh) - Enable GEM integration
  'user_gem_integration',

  // 2025-11-17 (yaron) - Multi domain
  'user_multi_domain',

  // 2024-08-29 (Ashutosh) - Enable new ORG onboarding experience PERMANENT FF
  'user_new_onboarding_experience',

  // 2023-12-12 (Gaurav) - Flag that retains search string after selecting an option
  // Not released at all, what's the plan?
  'user_provide_input_value_to_filterselect',

  // 2026-02-11 - Show Greenhouse OAuth integration card in Settings > Integrations
  'user_show_greenhouse_oauth_integration',

  // 2026-02-18 (yaron) - Show push schedule to ATS button on scheduled tasks
  'user_show_push_schedule_to_ats',

  // 2026-06-17 (ctriolo) - Show exact time ranges in schedule badge hover cards
  'user_show_schedule_badge_time_ranges',

  // 2024-11-19 (gaurav) - Enable smart recruiters extension
  'user_smart_recruiters_integration',

  // 2023-02-06 - Make end time not dependent on start time in time range picker.
  // Only on for Lyra.
  'user_time_range_picker_dont_coalesce_end_time',
  // keep-sorted:end
] as const;

export type FeatureFlagBool = (typeof BOOL_FEATURE_FLAGS)[number];
